/**
 * AuthContext — Single centralized Supabase auth subscription.
 * All components read auth state from this context instead of
 * creating their own onAuthStateChange listeners.
 */
import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { User } from "@supabase/supabase-js";
import { supabase, ensureUserSession } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { clearAllUserScopedStorage, clearScopedStorageForUser } from "@/hooks/useScopedStorageKey";
import { logger } from "@/utils/logger";
import { migrateGuestGoals } from "@/lib/bucketList";

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const hasShownWelcomeToast = useRef(false);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    logger.debug("🔐 AuthProvider initializing (single subscription)...");

    // 1. Set up auth state change listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      logger.debug("🔐 Auth state changed:", event);

      const newUser = session?.user ?? null;
      setUser(newUser);
      setLoading(false);

      // Set up session variables on sign-in (fire-and-forget, non-blocking)
      if (newUser && session && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
        setTimeout(async () => {
          try {
            await ensureUserSession();
          } catch (err) {
            logger.warn("⚠️ Session setup error (non-critical):", err);
          }
        }, 100);
      }

      // Move anything saved as a guest (goals, bucket list) into the account.
      if (newUser && event === 'SIGNED_IN') {
        setTimeout(async () => {
          const moved = await migrateGuestGoals(newUser.id);
          if (moved > 0) {
            toast({
              title: "Your saved items are safe ✨",
              description: `${moved} ${moved === 1 ? "item" : "items"} from this device moved to your account.`,
            });
          }
        }, 600);
      }

      // Share-to-signup attribution: if the visitor arrived via a shared
      // certificate link, credit that certificate on their new profile.
      if (newUser && event === 'SIGNED_IN') {
        try {
          const pendingCert = sessionStorage.getItem('pendingReferralCert');
          if (pendingCert) {
            sessionStorage.removeItem('pendingReferralCert');
            setTimeout(async () => {
              try {
                await supabase
                  .from('profiles')
                  .update({ referred_by_certificate_id: pendingCert })
                  .eq('id', newUser.id)
                  .is('referred_by_certificate_id', null);
              } catch (err) {
                logger.warn("⚠️ Referral attribution failed (non-critical):", err);
              }
            }, 500);
          }
        } catch {
          /* sessionStorage may be unavailable */
        }
      }

      // Show welcome toast only on actual sign-in, not session restores
      if (event === 'SIGNED_IN' && !hasShownWelcomeToast.current) {
        toast({
          title: "Welcome to Goal Tracker!",
          description: "You are now signed in.",
          duration: 3000,
        });
        hasShownWelcomeToast.current = true;
      }

      if (event === 'SIGNED_OUT') {
        hasShownWelcomeToast.current = false;
        // Clear every user-scoped local bucket so guest mode starts clean.
        clearAllUserScopedStorage();
        localStorage.removeItem('goal_tracker_quick_tasks');
        localStorage.removeItem('goal_tracker_family_bonding');
        localStorage.removeItem('goal_tracker_goals');
        localStorage.removeItem('guestGoals');
        sessionStorage.clear();
        localStorage.removeItem('goals-cache');
      }
    });

    // 2. THEN check for existing session
    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) throw error;

        const newUser = session?.user ?? null;
        setUser(newUser);

        if (newUser && session) {
          // Fire-and-forget session setup
          ensureUserSession().catch(err =>
            logger.warn("⚠️ Initial session setup failed (non-critical):", err)
          );
        }

        setLoading(false);
      } catch (error) {
        logger.error("❌ Auth initialization error:", error);
        setLoading(false);
      }
    };

    initializeAuth();

    return () => {
      subscription.unsubscribe();
    };
  }, []); // Empty deps — runs once

  const signOut = useCallback(async () => {
    try {
      // Clear every user-scoped local bucket so guest mode starts clean.
      clearScopedStorageForUser(user?.id);
      localStorage.removeItem('goal_tracker_quick_tasks');
      localStorage.removeItem('goal_tracker_family_bonding');
      localStorage.removeItem('goal_tracker_goals');
      localStorage.removeItem('guestGoals');
      sessionStorage.clear();
      localStorage.removeItem('goals-cache');
      localStorage.removeItem('user_avatar');
      localStorage.removeItem('user_color');

      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      hasShownWelcomeToast.current = false;
      toast({
        title: "Signed out successfully",
        description: "You have been signed out of your account.",
        duration: 3000,
      });
    } catch (error: any) {
      logger.error("❌ Sign out error:", error);
      toast({
        title: "Error signing out",
        description: error.message,
        variant: "destructive",
        duration: 3000,
      });
    }
  }, [toast, user?.id]);

  return (
    <AuthContext.Provider value={{ user, loading, signOut, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return ctx;
}
